import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardContent,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton
} from '@mui/material';
import { 
  Security,
  Speed,
  PlayArrow,
  TrendingUp,
  Star,
  Whatshot,
  Shield,
  Groups,
  Close as CloseIcon
} from '@mui/icons-material';
import '../styles/Home.css';

const Home = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('user'));
  const [news, setNews] = useState([]);
  const [selectedNews, setSelectedNews] = useState(null); // État pour la pop-up

  const scrollRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isDragOccurred, setIsDragOccurred] = useState(false); // Différencier drag et clic

  useEffect(() => {
    const sync = () => setIsLoggedIn(!!localStorage.getItem('user'));
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await fetch('https://updater.nexus-temporel.com/v1/launcher/srv_nexus/news');
        
        if (response.ok) {
          const signedData = await response.json();
          
          if (signedData.payload) {
            const decodeBase64Utf8 = (base64) => {
              const binString = atob(base64);
              const bytes = new Uint8Array(binString.length);
              for (let i = 0; i < binString.length; i++) {
                bytes[i] = binString.charCodeAt(i);
              }
              return new TextDecoder('utf-8').decode(bytes);
            };

            const decodedText = decodeBase64Utf8(signedData.payload);
            const parsedPayload = JSON.parse(decodedText);
            const itemsArray = parsedPayload.items || [];
            
            const stripHtml = (html) => {
              const tmp = document.createElement("DIV");
              tmp.innerHTML = html;
              return tmp.textContent || tmp.innerText || "";
            };

            const formattedNews = itemsArray.map(item => ({
              title: item.title || "Nouvelle Annonce",
              date: item.publishedAt ? new Date(item.publishedAt).toLocaleDateString('fr-FR') : "Récemment",
              description: item.bodyHtml ? stripHtml(item.bodyHtml).substring(0, 150) + "..." : "",
              fullHtml: item.bodyHtml || "<p>Aucun contenu détaillé disponible.</p>", // Sauvegarde du HTML brut
              tag: item.author || "Annonce",
              color: "#B67A3C"
            }));
            
            setNews(formattedNews);
          }
        } else {
          console.error("Erreur lors de la récupération des news : statut", response.status);
        }
      } catch (error) {
        console.error("Erreur réseau lors de la récupération des news :", error);
      }
    };

    fetchNews();
  }, []);

  // --- FONCTIONS DE GESTION DU DRAG ---
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setIsDragOccurred(false);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 1.5; // Multiplicateur de vitesse de défilement
    
    if (Math.abs(walk) > 5) {
      setIsDragOccurred(true);
    }
    
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const features = [
    {
      icon: <Speed fontSize="large" />,
      title: "Stabilité Temporelle",
      description: "Les failles sont maîtrisées. Uptime 99.9%, performances optimales même dans les zones de convergence.",
      color: "#B67A3C"
    },
    {
      icon: <Shield fontSize="large" />,
      title: "Gardiens du Temps",
      description: "Une équipe de modération vigilante protège l'intégrité des lignes temporelles.",
      color: "#C9CED6"
    },
    {
      icon: <Whatshot fontSize="large" />,
      title: "Fractures Équilibrées",
      description: "Des taux de progression pensés pour une montée en puissance épique et méritée.",
      color: "#F44336"
    },
    {
      icon: <Security fontSize="large" />,
      title: "Double Authentification",
      description: "Votre compte est protégé par une double authentification in-game. Sécurité renforcée pour jouer sans craindre les intrusions.",
      color: "#C9CED6"
    }
  ];

  const concepts = [
    { icon: '⚔️', title: 'Monocompte Cheat', description: 'Un seul compte par joueur, des stats boostées pour une progression épique et intense dans chaque combat.', color: '#F44336' },
    { icon: '♾️', title: 'Prestige Continu', description: 'Atteignez le niveau maximum et prestige sans jamais repasser niveau 1. Votre progression est définitive.', color: '#B67A3C' },
    { icon: '🔮', title: 'Upgrade d\'Items', description: 'Améliorez vos équipements et faites monter leur rareté. Forgez votre équipement parfait pièce par pièce.', color: '#00BCD4' },
    { icon: '📜', title: 'Quêtes Quotidiennes', description: 'Des défis renouvelés chaque jour pour maintenir la progression, gagner des récompenses exclusives et ne jamais stagner.', color: '#FF9800' },
    { icon: '🛡️', title: 'Worldboss', description: 'Des worldboss spawn aléatoirement et ne peuvent être vaincus qu\'en grande équipe. Préparez votre raid et frappez au bon moment.', color: '#4CAF50' },
    { icon: '💎', title: 'Fusion de Dofus', description: 'Fusionnez des artefacts pour obtenir des reliques encore plus puissantes. Un système de craft unique à Nexus.', color: '#C9CED6' },
    { icon: '🏆', title: 'PvP Capé', description: 'Affrontez les autres joueurs en combat capé. Seule la maîtrise de votre build compte — pas le stuff.', color: '#E91E63' },
    { icon: '🌾', title: 'Farm2Win', description: 'Aucun pay-to-win. Tout s\'obtient en jeu. Plus vous farmez, plus vous progressez — mérite avant tout.', color: '#8BC34A' },
  ];

  return (
    <Box className="home-container-v2">
      {/* Failles temporelles flottantes */}
      <Box className="floating-rifts-overlay" aria-hidden="true">
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((i) => (
          <img key={i} src="/assets/images/space.webp" alt="" className={`temporal-rift rift-${i}`} draggable="false" />
        ))}
      </Box>

      {/* Hero Section */}
      <Box className="hero-section-v2">
        <div className="hero-particles"></div>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box className="hero-content-v2">
                <Typography 
                  variant="h1" 
                  className="hero-title-v2"
                  sx={{ 
                    fontWeight: 900,
                    fontSize: { xs: '3rem', md: '5rem' },
                    background: 'linear-gradient(135deg, #B67A3C 0%, #E4E7EC 50%, #C9CED6 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    marginBottom: 2,
                    textShadow: '0 0 80px rgba(182, 122, 60, 0.5)',
                    letterSpacing: '2px'
                  }}
                >
                  Nexus
                </Typography>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.9)',
                    marginBottom: 4,
                    fontSize: { xs: '1.2rem', md: '1.8rem' },
                    fontWeight: 300,
                    letterSpacing: '1px'
                  }}
                >
                  L'Aventure Sans Limites
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {!isLoggedIn && (
                    <Button 
                      component={Link}
                      to="/signup"
                      variant="contained" 
                      size="large"
                      startIcon={<PlayArrow />}
                      className="cta-button-v2 primary"
                      sx={{
                        background: 'linear-gradient(135deg, #B67A3C 0%, #8D5A2A 100%)',
                        color: '#fff',
                        fontWeight: 700,
                        padding: '15px 40px',
                        fontSize: '1.2rem',
                        borderRadius: '50px',
                        boxShadow: '0 10px 30px rgba(182, 122, 60, 0.5)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #8D5A2A 0%, #734823 100%)',
                          transform: 'translateY(-3px)',
                          boxShadow: '0 15px 40px rgba(182, 122, 60, 0.7)'
                        }
                      }}
                    >
                      Commencer à Jouer
                    </Button>
                  )}
                  <Button 
                    component={Link}
                    to="/players"
                    variant="outlined" 
                    size="large"
                    startIcon={<TrendingUp />}
                    sx={{
                      borderColor: '#B67A3C',
                      color: '#E4E7EC',
                      fontWeight: 600,
                      padding: '15px 40px',
                      fontSize: '1.2rem',
                      borderRadius: '50px',
                      borderWidth: '2px',
                      '&:hover': {
                        borderColor: '#8D5A2A',
                        background: 'rgba(182, 122, 60, 0.1)',
                        borderWidth: '2px'
                      }
                    }}
                  >
                    Classement
                  </Button>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ textAlign: 'center', display: { xs: 'none', md: 'block' } }}>
              <Box className="mascot-container">
                <Box 
                  sx={{ 
                    width: '100%',
                    height: '500px',
                    background: 'radial-gradient(circle, rgba(182, 122, 60, 0.3) 0%, transparent 70%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                  }}
                >
                  <img 
                    src="/assets/images/Nexus.png"
                    alt="Nexus"
                    loading="lazy"
                    width="480"
                    draggable="false"
                    style={{ 
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: '480px',
                      filter: 'drop-shadow(0 0 60px rgba(182, 122, 60, 0.9))',
                      animation: 'float 3s ease-in-out infinite'
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Bar */}
      <Container maxWidth="lg" sx={{ marginTop: -6, position: 'relative', zIndex: 10 }}>
        <Paper 
          elevation={8}
          sx={{
            padding: 4,
            background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.95) 0%, rgba(31, 37, 68, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            border: '2px solid rgba(182, 122, 60, 0.3)',
            borderRadius: '20px'
          }}
        >
          <Grid container spacing={3}>
            {[
              { label: 'Nouveau contenu', value: '⚔️', icon: <Shield />, color: '#B67A3C' },
              { label: 'Farm2Win', value: '0 P2W', icon: <TrendingUp />, color: '#4CAF50' },
              { label: 'Double Auth In-Game', value: '2FA', icon: <Security />, color: '#C9CED6' },
              { label: 'Staff actif et à l\'écoute', value: '💬', icon: <Groups />, color: '#5865F2' }
            ].map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ color: stat.color, marginBottom: 1 }}>
                    {stat.icon}
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: stat.color, marginBottom: 0.5 }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ marginTop: 10, marginBottom: 10 }}>
        <Box sx={{ textAlign: 'center', marginBottom: 6 }}>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 800,
              marginBottom: 2,
              background: 'linear-gradient(135deg, #B67A3C 0%, #C9CED6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Pourquoi Nexus ?
          </Typography>
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Les fractures temporelles n'attendent que vous
          </Typography>
        </Box>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card 
                className="feature-card-v2"
                sx={{
                  height: '100%',
                  background: 'linear-gradient(145deg, rgba(31, 37, 68, 0.6) 0%, rgba(26, 26, 46, 0.6) 100%)',
                  border: `2px solid ${feature.color}30`,
                  borderRadius: '20px',
                  transition: 'all 0.4s ease',
                  '&:hover': {
                    transform: 'translateY(-15px) scale(1.02)',
                    borderColor: feature.color,
                    boxShadow: `0 20px 40px ${feature.color}40`
                  }
                }}
              >
                <CardContent sx={{ textAlign: 'center', padding: 4 }}>
                  <Box 
                    sx={{ 
                      width: 80, height: 80, borderRadius: '50%', background: `${feature.color}20`, display: 'flex',
                      alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', color: feature.color, border: `3px solid ${feature.color}50`
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, marginBottom: 2, color: '#fff' }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.7 }}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* News Section Dynamique (Drag to scroll) */}
      <Box sx={{ background: 'rgba(15, 23, 41, 0.5)', padding: '80px 0', overflow: 'hidden' }}>
        <Container maxWidth="lg" sx={{ marginBottom: 4 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" sx={{ fontWeight: 800, marginBottom: 2, color: '#fff' }}>
              📰 Dernières Actualités
            </Typography>
            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '1rem' }}>
              Glissez pour explorer, cliquez sur une actualité pour la lire en entier
            </Typography>
          </Box>
        </Container>

        {news.length > 0 && (
          <Box 
            className="news-marquee-container"
            ref={scrollRef}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            sx={{
              display: 'flex',
              overflowX: 'auto',
              cursor: isDragging ? 'grabbing' : 'grab',
              scrollBehavior: isDragging ? 'auto' : 'smooth', // Retire la fluidité native pendant le drag
              userSelect: 'none', // Empêche la sélection en glissant
              paddingBottom: '20px', // Laisse de la place pour les ombres au hover
              '&::-webkit-scrollbar': { display: 'none' }, // Cache la scrollbar
              msOverflowStyle: 'none', 
              scrollbarWidth: 'none',
            }}
          >
            <Box 
              className="news-marquee-track"
              sx={{
                display: 'flex',
                gap: '20px',
                padding: '0 20px',
                animationPlayState: isDragging ? 'paused' : 'running'
              }}
            >
              {[...news, ...news].map((item, index) => (
                <Box 
                  key={index} 
                  className="news-card-wrapper"
                  onClick={(e) => {
                    if (isDragOccurred) {
                      e.preventDefault();
                      return;
                    }
                    setSelectedNews(item);
                  }}
                  sx={{ minWidth: '350px', maxWidth: '400px' }} // Fixe la taille pour le scroll
                >
                  <Card 
                    sx={{
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(145deg, rgba(31, 37, 68, 0.8) 0%, rgba(26, 26, 46, 0.8) 100%)',
                      border: '1px solid rgba(182, 122, 60, 0.3)',
                      borderRadius: '16px',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:hover': {
                        transform: !isDragging ? 'translateY(-5px)' : 'none',
                        borderColor: item.color || "#00BCD4",
                        boxShadow: !isDragging ? `0 10px 20px ${item.color}20` : 'none'
                      }
                    }}
                  >
                    <CardContent sx={{ padding: 3, flexGrow: 1, pointerEvents: 'none' /* Empêche les bugs de sélection sur le texte */}}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                        <Chip 
                          label={item.tag}
                          size="small"
                          sx={{ 
                            background: `${item.color || "#00BCD4"}30`,
                            color: item.color || "#00BCD4",
                            fontWeight: 600,
                            border: `1px solid ${item.color || "#00BCD4"}`
                          }}
                        />
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                          {item.date}
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, marginBottom: 2, color: '#fff', lineHeight: 1.3 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6 }}>
                        {item.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Concept Section */}
      <Container maxWidth="lg" sx={{ paddingTop: 10, paddingBottom: 10 }}>
        <Box sx={{ textAlign: 'center', marginBottom: 6 }}>
          <Typography
            variant="h3"
            sx={{ fontWeight: 800, marginBottom: 2, background: 'linear-gradient(135deg, #B67A3C 0%, #E4E7EC 50%, #C9CED6 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
          >
            Le Concept Nexus
          </Typography>
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.6)', maxWidth: 600, margin: '0 auto' }}>
            Nexus réinventé — un serveur cheat monocompte pensé pour le fun, la progression permanente et le défi.
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {concepts.map((item, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, rgba(26,10,46,0.9) 0%, rgba(15,23,41,0.95) 100%)',
                  border: `1px solid ${item.color}30`,
                  borderRadius: '16px',
                  height: '100%',
                  transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
                  '&:hover': { transform: 'translateY(-6px)', boxShadow: `0 12px 35px ${item.color}30`, borderColor: `${item.color}70` }
                }}
              >
                <CardContent sx={{ padding: '24px 20px' }}>
                  <Typography sx={{ fontSize: '2.2rem', marginBottom: 1.5, lineHeight: 1 }}>{item.icon}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: item.color, marginBottom: 1, fontSize: '1rem' }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, fontSize: '0.85rem' }}>
                    {item.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Final */}
      <Box sx={{ position: 'relative', overflow: 'hidden', background: 'linear-gradient(180deg, transparent 0%, rgba(6,0,10,0.6) 40%, #06000a 100%)', paddingTop: 10, paddingBottom: 12, textAlign: 'center' }}>
        <Box sx={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 80% 60% at 50% 60%, rgba(182,122,60,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '200px', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(182,122,60,0.6), rgba(201,206,214,0.4), rgba(182,122,60,0.6), transparent)' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
          <Typography variant="overline" sx={{ color: 'rgba(182,122,60,0.8)', letterSpacing: '4px', fontSize: '0.75rem', fontWeight: 700, display: 'block', marginBottom: 2 }}>
            Monocompte — Cheat — Farm2Win
          </Typography>
          <Typography variant="h2" sx={{ fontWeight: 900, marginBottom: 3, fontSize: { xs: '2rem', md: '3.2rem' }, background: 'linear-gradient(135deg, #fff 0%, #E4E7EC 50%, #C9CED6 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', lineHeight: 1.15 }}>
            Prêt à écrire<br />votre légende ?
          </Typography>
          <Typography variant="h6" sx={{ marginBottom: 5, color: 'rgba(255,255,255,0.5)', maxWidth: 520, margin: '0 auto 40px', fontWeight: 400, fontSize: '1rem', lineHeight: 1.7 }}>
            Nexus est fait pour ceux qui veulent vivre l'aventure à fond — sans reset, sans limite, sans pay-to-win.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            {!isLoggedIn && (
              <Button component={Link} to="/signup" variant="contained" size="large" startIcon={<PlayArrow />} sx={{ background: 'linear-gradient(135deg, #B67A3C 0%, #8D5A2A 100%)', color: '#fff', fontWeight: 700, padding: '16px 48px', fontSize: '1.1rem', borderRadius: '50px', boxShadow: '0 8px 30px rgba(182,122,60,0.45)', '&:hover': { background: 'linear-gradient(135deg, #8D5A2A 0%, #734823 100%)', transform: 'translateY(-3px)', boxShadow: '0 14px 40px rgba(182,122,60,0.65)' } }}>
                Créer mon Compte
              </Button>
            )}
            <Button component={Link} to="/join-us" variant="outlined" size="large" sx={{ borderColor: 'rgba(201,206,214,0.4)', color: 'rgba(201,206,214,0.85)', fontWeight: 600, padding: '16px 40px', fontSize: '1.1rem', borderRadius: '50px', borderWidth: '1.5px', '&:hover': { borderColor: '#C9CED6', background: 'rgba(201,206,214,0.07)', borderWidth: '1.5px' } }}>
              Comment jouer ?
            </Button>
          </Box>
        </Container>
      </Box>

      {/* --- POP-UP ACTUALITÉ (DIALOG) --- */}
      <Dialog 
        open={Boolean(selectedNews)} 
        onClose={() => setSelectedNews(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(145deg, #1F2544 0%, #1A1A2E 100%)',
            border: '1px solid rgba(182, 122, 60, 0.4)',
            borderRadius: '16px',
            color: '#fff'
          }
        }}
      >
        {selectedNews && (
          <>
            <DialogTitle sx={{ m: 0, p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Chip 
                  label={selectedNews.tag}
                  size="small"
                  sx={{ 
                    background: `${selectedNews.color}30`,
                    color: selectedNews.color,
                    fontWeight: 600,
                    marginBottom: 1,
                    border: `1px solid ${selectedNews.color}`
                  }}
                />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', marginTop: 0.5 }}>
                  Publié le {selectedNews.date}
                </Typography>
              </Box>
              <IconButton
                aria-label="close"
                onClick={() => setSelectedNews(null)}
                sx={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  '&:hover': { color: '#B67A3C' },
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            
            <DialogContent dividers sx={{ borderColor: 'rgba(182, 122, 60, 0.2)', padding: '32px' }}>
              <Box 
                dangerouslySetInnerHTML={{ __html: selectedNews.fullHtml }} 
                sx={{
                  color: 'rgba(255, 255, 255, 0.85)',
                  lineHeight: 1.7,
                  fontSize: '1.05rem',
                  '& h1, & h2, & h3': { color: '#B67A3C', marginTop: 0, fontWeight: 700 },
                  '& a': { color: '#00BCD4', textDecoration: 'none' },
                  '& a:hover': { textDecoration: 'underline' },
                  '& img': { maxWidth: '100%', height: 'auto', borderRadius: '8px', margin: '16px 0' },
                  '& blockquote': { 
                    borderLeft: '4px solid #B67A3C', 
                    padding: '8px 16px', 
                    margin: '1em 0', 
                    backgroundColor: 'rgba(182,122,60,0.1)',
                    borderRadius: '0 8px 8px 0'
                  },
                  '& ul, & ol': { paddingLeft: '24px' },
                  '& li': { marginBottom: '8px' },
                  '& p': { marginBottom: '16px' }
                }}
              />
            </DialogContent>
            <Box sx={{ padding: 2, textAlign: 'right' }}>
              <Button onClick={() => setSelectedNews(null)} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}>
                Fermer
              </Button>
            </Box>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Home;